import { apiClient } from "./client";
import { DestinatarioProposto, EsitoInvio } from "./report";
import { RiferimentiOdoo } from "../types";

export interface AccessoRischio {
  finestre?: string;
  lucernai?: string;
  vetrine?: string;
  altro?: string;
}

export interface PreseElettriche {
  schuko?: boolean;
  tradizionale?: boolean;
  punto_rete?: boolean;
}

export interface Sopralluogo extends RiferimentiOdoo {
  id: string;
  in_ritardo?: boolean;
  da_completare: boolean;
  /** Fase 3.4: true quando compilato da un utente cliente. */
  compilato_da_cliente: boolean;
  /** Fase 3.4: utente cliente assegnato a compilarlo. */
  assegnato_utente_id: string | null;
  /**
   * Nome dell'assegnatario, risolto dal backend. `null` anche quando
   * `assegnato_utente_id` è valorizzato, se quell'utente è una casella
   * aziendale senza nome: sono due casi diversi e la UI li distingue.
   */
  assegnato_nome?: string | null;
  /** Centro Assistenza o Istituto di Vigilanza proprietario, risolto dal backend. */
  organizzazione?: string | null;
  compilatore_nome: string | null;
  societa_appartenenza: string | null;
  data_sopralluogo: string;
  centro_assistenza_id: string | null;
  istituto_vigilanza_id: string | null;

  cliente_id: string | null;
  /** Sede specifica dell'anagrafica (propedeutico alla vista aggregata per sede). */
  sede_id: string | null;
  ragione_sociale_cliente: string | null;
  insegna_cliente: string | null;
  tipo_attivita: string | null;

  regione: string | null;
  provincia: string | null;
  indirizzo: string | null;
  ubicazione_negozio: string | null;
  note_ubicazione: string | null;

  tipo_locale: string | null;
  numero_locali: number | null;
  tipo_ingresso_uscita: string | null;
  note_locali: string | null;

  numero_accessi_locale: string | null;
  tipo_accesso_locale: string | null;
  installazione_possibile: string | null;
  note_installazione: string | null;
  caratteristiche_pareti: string | null;
  note_pareti: string | null;
  caratteristiche_pavimento: string | null;
  accessi_rischio: AccessoRischio | null;
  note_accessi_rischio: string | null;
  protezioni_strutturali: AccessoRischio | null;
  note_protezioni: string | null;

  prese_elettriche: PreseElettriche | null;
  numero_prese: string | null;
  collegamento_internet: string | null;
  note_elettrico_rete: string | null;

  allarme_presente: boolean | null;
  allarme_marca: string | null;
  allarme_modello: string | null;
  allarme_ultima_manutenzione: string | null;
  allarme_contratto_manutenzione: string | null;
  sensori_locale_cassaforte: string | null;
  altri_sistemi_sicurezza: boolean | null;
  collegamento_vigilanza: boolean | null;
  istituto_vigilanza_nome: string | null;
  collegamento_forze_ordine: boolean | null;
  note_antintrusione: string | null;

  posizione_ostacoli: boolean | null;
  note_ostacoli: string | null;
  facile_accesso_installatore: boolean | null;
  facile_accesso_vigilanza: boolean | null;
  confini_posizione: string | null;
  accesso_locali: string | null;
  modello_da_installare: string | null;
  dispositivo_catalogo_id: string | null;
  validita_installazione: number | null;

  esito_ispezione_locali: boolean | null;
  esito_locale_cassaforte: boolean | null;
  esito_impianto_elettrico: boolean | null;
  esito_impianto_antintrusione: boolean | null;
  esito_posizione_cassaforte: boolean | null;
  disegno_locali_effettuato: boolean | null;

  stato: string;
  note: string | null;
  created_at: string;
}

export interface SopralluogoFilters {
  stato?: string;
  cliente_id?: string;
  mese?: number; // 1-12
  anno?: number;
  ritardo?: boolean;
  /**
   * Ricerca testuale su insegna, ragione sociale e nome della sede.
   *
   * Sotto i 2 caratteri il backend la ignora; sopra, tronca a 20 risultati —
   * serve a un autocomplete, non a sfogliare l'elenco.
   */
  q?: string;
}

export async function listSopralluoghi(filters: SopralluogoFilters = {}): Promise<{ records: Sopralluogo[] }> {
  const { data } = await apiClient.get<{ records: Sopralluogo[] }>("/sopralluoghi", { params: filters });
  return data;
}

export async function getSopralluogo(id: string): Promise<{ record: Sopralluogo }> {
  const { data } = await apiClient.get<{ record: Sopralluogo }>(`/sopralluoghi/${id}`);
  return data;
}

export async function createSopralluogo(payload: Partial<Sopralluogo>): Promise<{ record: Sopralluogo }> {
  const { data } = await apiClient.post<{ record: Sopralluogo }>("/sopralluoghi", payload);
  return data;
}

export async function updateSopralluogo(id: string, payload: Partial<Sopralluogo>): Promise<{ record: Sopralluogo }> {
  const { data } = await apiClient.patch<{ record: Sopralluogo }>(`/sopralluoghi/${id}`, payload);
  return data;
}

export async function deleteSopralluogo(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/sopralluoghi/${id}`);
  return data;
}

// --- Condivisione (riservata all'admin) --------------------------------------
export interface CondivisioneSopralluogo {
  id: string;
  centro_assistenza_id: string;
  centro_assistenza: { id: string; nome: string };
  condiviso_at: string;
}

export async function listCondivisioni(sopralluogoId: string): Promise<{ condivisioni: CondivisioneSopralluogo[] }> {
  const { data } = await apiClient.get<{ condivisioni: CondivisioneSopralluogo[] }>(
    `/sopralluoghi/${sopralluogoId}/condivisioni`
  );
  return data;
}

export async function condividiSopralluogo(
  sopralluogoId: string,
  centroAssistenzaIds: string[]
): Promise<{ condivisioni: CondivisioneSopralluogo[] }> {
  const { data } = await apiClient.post<{ condivisioni: CondivisioneSopralluogo[] }>(
    `/sopralluoghi/${sopralluogoId}/condividi`,
    { centro_assistenza_ids: centroAssistenzaIds }
  );
  return data;
}

export async function revocaCondivisione(sopralluogoId: string, centroAssistenzaId: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(
    `/sopralluoghi/${sopralluogoId}/condividi/${centroAssistenzaId}`
  );
  return data;
}

// --- Handoff a centro (Fase 3.5) --------------------------------------------

/**
 * Handoff (Fase 3.5) e riassegnazione: sposta un sopralluogo fra
 * organizzazioni. Copre tutte e tre le transizioni —
 * un centro, un istituto, oppure nessuno dei due — perché è l'unico endpoint
 * autorizzato a farlo: la PATCH generica scarta quei campi di proposito.
 *
 * Se l'assegnatario attuale non appartiene alla nuova organizzazione il
 * backend lo azzera, e lo dichiara nell'audit.
 */
export async function assegnaCentro(
  sopralluogoId: string,
  organizzazione: { centro_assistenza_id?: string | null; istituto_vigilanza_id?: string | null }
): Promise<{ record: Sopralluogo }> {
  const { data } = await apiClient.patch<{ record: Sopralluogo }>(`/sopralluoghi/${sopralluogoId}/assegna-centro`, {
    centro_assistenza_id: organizzazione.centro_assistenza_id ?? null,
    istituto_vigilanza_id: organizzazione.istituto_vigilanza_id ?? null,
  });
  return data;
}


// ============================================================================
// Invio per email — il PDF viene rigenerato lato server dai dati correnti, lo
// STESSO che si scarica: nessuna copia congelata.
// ============================================================================

/** Gli indirizzi che l'app propone per questo record — possono essere zero. */
export async function destinatariPropostiSopralluogo(id: string): Promise<DestinatarioProposto[]> {
  const { data } = await apiClient.get<{ destinatari: DestinatarioProposto[] }>(`/sopralluoghi/${id}/destinatari`);
  return data.destinatari;
}

/**
 * Manda il documento agli indirizzi indicati, uno per destinatario.
 *
 * Risponde 200 anche quando qualche invio fallisce: l'esito e' per destinatario
 * e va letto, non dedotto dallo stato HTTP.
 */
export async function inviaSopralluogo(id: string, destinatari: string[], nota?: string): Promise<EsitoInvio> {
  const { data } = await apiClient.post<EsitoInvio>(`/sopralluoghi/${id}/invia`, { destinatari, nota });
  return data;
}
